import { ArrowBack } from "@mui/icons-material";
import { IconButton, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import { useGetDataObjectByIdQuery } from "@store/api/dataApi";
import { getResourceErrorStatus } from "@store/api/types";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ResourceAccessErrorPanel from "@components/ResourceAccessErrorPanel";

import { DATA_EXPLORER_ROUTE, DATA_TREE_SEGMENT } from "@helpers/constants";
import { DATA_EXPLORER_EVENTS } from "@helpers/events";

import DataColumn from "../components/DataColumn";
import Loading from "../components/Loading";
import SiteTitle from "../components/SiteTitle";
import type { ClientFile } from "../helpers/utils";

const Page = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  height: "100%",
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(4, 9),
  },
}));

const Header = styled("header")<{ canGoBack?: boolean }>(
  ({ theme, canGoBack }) => ({
    display: "grid",
    gridAutoFlow: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "start",
    [theme.breakpoints.up("md")]: {
      marginLeft: canGoBack ? `calc(-44px - ${theme.spacing(2)})` : 0,
    },
  }),
);

const FileBrowser = styled(Paper)(() => ({
  display: "grid",
  gridAutoFlow: "column",
  height: "100%",
  overflowX: "scroll",
  justifyContent: "start",
  "&.empty": {
    justifyContent: "stretch",
  },
}));

export function useCurrentPath(props?: { prefixWithDot: boolean }) {
  const location = useLocation();
  const currentFilepathIndex = location.pathname.indexOf(DATA_TREE_SEGMENT);
  const encodedPath =
    currentFilepathIndex > 0
      ? location.pathname.slice(currentFilepathIndex + DATA_TREE_SEGMENT.length)
      : "";
  const path = encodedPath
    .split("/")
    .map((segment) => (segment ? decodeURIComponent(segment) : segment))
    .join("/");
  return props?.prefixWithDot ? `.${path}` : path;
}

export const DataContext = React.createContext<{
  file?: ClientFile;
  setFile?: React.Dispatch<React.SetStateAction<ClientFile | undefined>>;
}>({});

export const Data = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const canGoBack = location.state?.canGoBack;
  const { dataId } = useParams<{ dataId: string }>();
  const [file, setFile] = useState<ClientFile>();

  const currentPath = useCurrentPath();
  const columns = currentPath
    .split("/")
    .reduce<
      string[]
    >((columns, segment, index) => columns.concat(index > 0 ? [[columns[index - 1], segment].join("/")] : [segment]), []);

  const fileBrowser = useRef<HTMLDivElement>(null);

  const {
    data: currentData,
    isLoading: loading,
    error,
  } = useGetDataObjectByIdQuery({ id: dataId ?? "" }, { skip: !dataId });

  useEffect(() => {
    const currentList = document.querySelector(`.column:last-child`);
    if (currentList) {
      currentList.scrollIntoView();
    }
  }, [columns]);

  // Check the error before the loading/no-data guard: on a 403 there's no data
  // and we're not loading, so the guard below would otherwise spin forever.
  if (error) {
    return (
      <Page>
        <ResourceAccessErrorPanel
          status={getResourceErrorStatus(error)}
          resourceLabel="data"
          resourceId={dataId}
          backTo={DATA_EXPLORER_ROUTE}
          backLabel="Back to data explorer"
        />
      </Page>
    );
  }

  if (loading || !currentData) {
    return <Loading />;
  }

  return (
    <DataContext.Provider value={{ file, setFile }}>
      <Page>
        <Header canGoBack={canGoBack}>
          {canGoBack && (
            <IconButton
              onClick={() => {
                tracker.event(DATA_EXPLORER_EVENTS.DETAIL_BACK_CLICKED, dataId);
                navigate(-1);
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          <SiteTitle
            subtitle={
              <Typography variant="h3">
                {currentData.name ?? currentData.id}
              </Typography>
            }
          />
        </Header>
        <FileBrowser ref={fileBrowser}>
          {columns.map((path, index) => (
            <DataColumn
              key={path}
              dataId={dataId ?? ""}
              path={path}
              index={columns.length - 1 - index}
              activeSegment={
                columns[index + 1] && columns[index + 1].split(`${path}/`)[1]
              }
            />
          ))}
        </FileBrowser>
      </Page>
    </DataContext.Provider>
  );
};

export default Data;
